// src/components/phases/GameFinished.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import PlayerName from '../PlayerName';
import Footer from '../Footer';
import './GameFinished.css';

/**
 * GameFinished Component
 * Displays the final game results and winner(s)
 */
function GameFinished({
  players,
  scores,
  totalRoundsPlayed,
  round,
  roomCreatorPlayerId,
  roomCode,
  onLeaveGame,
}) {
  const navigate = useNavigate();

  // Filter out spectators - only active players can appear in rankings
  const activePlayers = players.filter((p) => !p.isSpectator);

  const sortedPlayers = [...activePlayers].sort((a, b) => {
    const scoreA = scores[a?.id] || scores[a?.socketId] || 0;
    const scoreB = scores[b?.id] || scores[b?.socketId] || 0;
    return scoreB - scoreA;
  });

  const winner = sortedPlayers[0];
  const winnerScore = scores[winner?.id] || scores[winner?.socketId] || 0;

  // Check for tie situations
  const topScore = winnerScore;
  const winners = sortedPlayers.filter((player) => {
    const playerScore = scores[player?.id] || scores[player?.socketId] || 0;
    return playerScore === topScore;
  });

  const isTie = winners.length > 1;

  // #299: standard competition ranking (ties share a rank; the next distinct
  // score skips ranks accordingly - 1,2,2,4 not 1,2,3,4). Used instead of raw
  // array index for medal/border assignment below, so tied players never get
  // arbitrarily split into different medals.
  const ranks = [];
  sortedPlayers.forEach((player, index) => {
    if (index === 0) {
      ranks.push(1);
      return;
    }
    const prevPlayer = sortedPlayers[index - 1];
    const prevScore = scores[prevPlayer?.id] || scores[prevPlayer?.socketId] || 0;
    const currentScore = scores[player?.id] || scores[player?.socketId] || 0;
    ranks.push(currentScore === prevScore ? ranks[index - 1] : index + 1);
  });

  // Get podium positions (top 3)
  const podiumPositions = sortedPlayers.slice(0, 3);

  return (
    <div className="game-finished-container">
      <div className="celebration-header">
        <div className="trophy-icon">🏆</div>
        <h1 className="game-complete-title">Game Complete!</h1>
        <div className="confetti">🎉</div>
      </div>

      <div className="winner-section">
        {isTie ? (
          <div className="tie-announcement">
            <h2 className="tie-title">It's a Tie! 🤝</h2>
            <div className="tied-winners">
              {winners.map((player, index) => (
                <div key={player?.id || index} className="tied-winner">
                  <span className="winner-name">
                    {player?.username || 'Unknown Player'}
                    {player?.isBot && ' 🤖'}
                  </span>
                  <span className="winner-score">
                    {winnerScore} Buffalo Chip{winnerScore !== 1 ? 's' : ''}
                  </span>
                </div>
              ))}
            </div>
            <p className="tie-message">Congratulations to all winners!</p>
          </div>
        ) : (
          <div className="single-winner">
            <div className="judge-icon">⚖️</div>
            <h2 className="winner-title">Champion</h2>
            <div className="winner-name">
              <PlayerName
                player={winner}
                roomCreatorPlayerId={roomCreatorPlayerId}
                showOwner={true}
                showCzar={false}
              />
            </div>
            <div className="winner-final-score">
              <span className="score-number">{winnerScore}</span>
              <span className="score-label">Buffalo Chips</span>
            </div>
            <p className="victory-message">Outstanding performance! 🌟</p>
          </div>
        )}
      </div>

      <div className="podium-section">
        <h3 className="podium-title">Final Rankings</h3>
        <div className="podium">
          {podiumPositions.map((player, index) => {
            const playerScore = scores[player?.id] || scores[player?.socketId] || 0;
            const position = ranks[index];
            const isWinner = position === 1;

            return (
              <div
                key={player?.id || index}
                className={`podium-position position-${position} ${isWinner ? 'winner-position' : ''}`}
              >
                <div className="position-number">
                  {position === 1 && '🥇'}
                  {position === 2 && '🥈'}
                  {position === 3 && '🥉'}
                  {position > 3 && `#${position}`}
                </div>
                <div className="player-info">
                  <div className="player-name">
                    {player?.username || 'Unknown Player'}
                    {player?.isBot && ' 🤖'}
                    {player?.isRemoved && ' (Removed)'}
                  </div>
                  <div className="player-final-score">
                    {playerScore} Chip{playerScore !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {sortedPlayers.length > 3 && (
        <div className="full-scoreboard">
          <h4 className="scoreboard-title">Complete Results</h4>
          <div className="full-rankings">
            {sortedPlayers.slice(3).map((player, index) => {
              const playerScore = scores[player?.id] || scores[player?.socketId] || 0;
              const position = ranks[index + 3];

              return (
                <div key={player?.id || index} className="ranking-item">
                  <span className="rank-number">#{position}</span>
                  <span className="rank-player-name">
                    {player?.username || 'Unknown Player'}
                    {player?.isBot && ' 🤖'}
                    {player?.isRemoved && ' (Removed)'}
                  </span>
                  <span className="rank-score">{playerScore}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="game-stats">
        <div className="stat-item">
          <span className="stat-number">{totalRoundsPlayed || round - 1}</span>
          <span className="stat-label">Rounds Played</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{activePlayers.length}</span>
          <span className="stat-label">Players</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{topScore}</span>
          <span className="stat-label">Winning Chips</span>
        </div>
      </div>

      <div className="action-buttons">
        <button onClick={onLeaveGame} className="play-again-btn">
          <span className="btn-icon">🎮</span>
          Play Again
        </button>
        <button onClick={() => navigate('/')} className="home-btn">
          <span className="btn-icon">🏠</span>
          Home
        </button>
      </div>

      <div className="game-footer">
        <p className="thanks-message">Thanks for playing Carnivores Adapting Terribly!</p>
        <p className="room-info">Room: {roomCode}</p>
      </div>

      {/* #297: /game-room hides the site Footer globally (App.js) to stay out of
          the way during active gameplay, but that also swallows this results
          screen - the moment players are most likely to want it (e.g. donate link). */}
      <Footer />
    </div>
  );
}

export default GameFinished;
